import React, { useState, useEffect } from 'react';
import { GlossaryTerm as GlossaryTermType, GlossaryCategory, GlossarySourceCategory } from '../../types/glossary';
import { glossaryService } from '../../services/glossaryService';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';
import { useAuth } from '../../hooks/useAuth';
import { GlossaryTerm } from '../../components/glossary/GlossaryTerm';
import { LayoutSelector } from '../../components/glossary/LayoutSelector';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, Edit, ChevronDown, ChevronUp, Search, Star, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from '../../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

/**
 * Favorites page for the Glossary app
 */
const GlossaryFavorites: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<GlossaryCategory[]>([]);
  const [favoriteTerms, setFavoriteTerms] = useState<Record<string, GlossaryTermType[]>>({});
  const [expandedTerms, setExpandedTerms] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // View mode state - default to Grid View (tooltip)
  const [viewMode, setViewMode] = useState<'compact' | 'tooltip' | 'expanded'>('tooltip');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTermType | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sourceCategories, setSourceCategories] = useState<GlossarySourceCategory[]>([]);
  
  // Dialog states
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<GlossaryCategory | null>(null);

  // Fetch source categories for name mapping
  useEffect(() => {
    const fetchSourceCategories = async () => {
      try {
        const allCategories = await glossaryCategoryService.getActiveCategories();
        setSourceCategories(allCategories);
      } catch (err) {
        console.error('Error fetching source categories:', err);
      }
    };

    fetchSourceCategories();
  }, []);

  // Fetch categories and favorite terms
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch categories
        const userCategories = await glossaryService.getCategories(user.uid);
        setCategories(userCategories);
        
        // Create default "My Favorites" category if it doesn't exist
        if (userCategories.length === 0) {
          const categoryId = await glossaryService.createCategory(
            'My Favorites',
            user.uid,
            user.displayName || 'Unknown User',
            user.photoURL || undefined
          );
          
          // Fetch categories again to include the new one
          const updatedCategories = await glossaryService.getCategories(user.uid);
          setCategories(updatedCategories);
        }
        
        // Fetch favorite terms for each category
        const termsMap: Record<string, GlossaryTermType[]> = {};
        
        for (const category of userCategories) {
          const terms: GlossaryTermType[] = [];
          
          for (const termId of category.termIds) {
            const term = await glossaryService.getGlossaryTermById(termId);
            if (term) {
              terms.push(term);
            }
          }
          
          termsMap[category.id] = terms;
        }
        
        setFavoriteTerms(termsMap);
        setError(null);
      } catch (err) {
        console.error('Error fetching favorites:', err);
        setError('Failed to load favorites');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFavorites();
  }, [user]);

  // Handle creating a new category
  const handleCreateCategory = async () => {
    if (!user || !newCategoryName.trim()) return;
    
    try {
      const categoryId = await glossaryService.createCategory(
        newCategoryName.trim(),
        user.uid,
        user.displayName || 'Unknown User',
        user.photoURL || undefined
      );
      
      // Fetch categories again to include the new one
      const updatedCategories = await glossaryService.getCategories(user.uid);
      setCategories(updatedCategories);
      
      // Initialize empty terms array for the new category
      setFavoriteTerms(prev => ({
        ...prev,
        [categoryId]: []
      }));
      
      toast({
        title: "Category Created",
        description: `"${newCategoryName}" has been created.`,
      });
      
      setNewCategoryName('');
      setShowNewCategoryDialog(false);
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Error",
        description: "Failed to create category.",
        variant: "destructive",
      });
    }
  };

  // Handle updating a category
  const handleUpdateCategory = async () => {
    if (!selectedCategory || !newCategoryName.trim()) return;
    
    try {
      await glossaryService.updateCategory(selectedCategory.id, {
        name: newCategoryName.trim()
      });
      
      // Update local state
      setCategories(prev => 
        prev.map(cat => 
          cat.id === selectedCategory.id 
            ? { ...cat, name: newCategoryName.trim() } 
            : cat
        )
      );
      
      toast({
        title: "Category Updated",
        description: `Category has been renamed to "${newCategoryName}".`,
      });
      
      setNewCategoryName('');
      setSelectedCategory(null);
      setShowEditCategoryDialog(false);
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category.",
        variant: "destructive",
      });
    }
  };

  // Handle deleting a category
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    try {
      await glossaryService.deleteCategory(selectedCategory.id);
      
      // Update local state
      setCategories(prev => prev.filter(cat => cat.id !== selectedCategory.id));
      setFavoriteTerms(prev => {
        const newMap = { ...prev };
        delete newMap[selectedCategory.id];
        return newMap;
      });
      
      toast({
        title: "Category Deleted",
        description: `"${selectedCategory.name}" has been deleted.`,
      });
      
      setSelectedCategory(null);
      setShowDeleteCategoryDialog(false);
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Error",
        description: "Failed to delete category.",
        variant: "destructive",
      });
    }
  };

  // Toggle term expansion
  const toggleTermExpansion = (termId: string) => {
    setExpandedTerms(prev => ({
      ...prev,
      [termId]: !prev[termId]
    }));
  };

  // Handle removing a term from favorites
  const handleRemoveFavorite = async (termId: string) => {
    if (!user) return;
    
    try {
      await glossaryService.removeFavorite(termId, user.uid);
      
      // Update local state
      setFavoriteTerms(prev => {
        const newMap = { ...prev };
        
        for (const categoryId in newMap) {
          newMap[categoryId] = newMap[categoryId].filter(term => term.id !== termId);
        }
        
        return newMap;
      });
      
      // Update categories
      const updatedCategories = await glossaryService.getCategories(user.uid);
      setCategories(updatedCategories);
      
      toast({
        title: "Removed from Favorites",
        description: "Term has been removed from favorites.",
        variant: "success",
        className: "bg-green-600 text-white border-green-700", // Custom styling
        duration: 2000, // 2 seconds
      });
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast({
        title: "Error",
        description: "Failed to remove term from favorites.",
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700", // Custom styling
        duration: 2000, // 2 seconds
      });
    }
  };

  if (!user) {
    return (
      <div className="p-4 md:p-8">
        <Card className="border border-gray-700">
          <CardHeader className="bg-gray-800">
            <CardTitle>Sign In Required</CardTitle>
          </CardHeader>
          <CardContent className="bg-gray-800/90">
            <p className="text-gray-300">Please sign in to view your favorites.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">My Favorites</h1>
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="border border-gray-800 rounded-lg overflow-hidden">
              <div className="bg-gray-800 p-4">
                <Skeleton className="h-6 w-1/3" />
              </div>
              <div className="p-4">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
// Helper functions for the view modes
  const getAllFavoriteTerms = (): GlossaryTermType[] => {
    const allTerms: GlossaryTermType[] = [];
    Object.values(favoriteTerms).forEach(terms => {
      allTerms.push(...terms);
    });
    return allTerms;
  };

  const getFilteredTerms = (): GlossaryTermType[] => {
    const allTerms = getAllFavoriteTerms();
    if (!searchQuery.trim()) return allTerms;
    
    const query = searchQuery.toLowerCase();
    return allTerms.filter(term =>
      term.term.toLowerCase().includes(query) ||
      term.content.toLowerCase().includes(query)
    );
  };

  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return 'General';
    
    // First try to use the categoryName if it exists
    const term = getAllFavoriteTerms().find(t => t.categoryId === categoryId);
    if (term?.categoryName) return term.categoryName;
    
    // Otherwise look up in source categories array
    const category = sourceCategories.find(cat => cat.id === categoryId);
    return category?.name || categoryId;
  };

  const openTermDialog = (term: GlossaryTermType) => {
    setSelectedTerm(term);
    setIsDialogOpen(true);
  };

  const toggleFavorite = async (termId: string) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to manage favorites",
        variant: "destructive",
      });
      return;
    }

    // Since this is the favorites page, we only remove from favorites
    await handleRemoveFavorite(termId);
  };

  const filteredTerms = getFilteredTerms();
  const totalTerms = getAllFavoriteTerms().length;

  return (
    <div className="px-4 md:px-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Favorites</h1>
        <LayoutSelector currentLayout={viewMode} onLayoutChange={setViewMode} />
      </div>
      
      {error ? (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      ) : totalTerms === 0 ? (
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            You don't have any favorite terms yet.
          </p>
        </div>
      ) : (
        <div className="mb-4">
          {/* Search bar and term count */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-gray-800 dark:text-gray-300">
                  {filteredTerms.length} favorite terms
                </div>
              </div>
              
              <div className="relative flex-1 md:max-w-md">
                <Input
                  type="text"
                  placeholder="Search favorite terms..."
                  className="pr-10 bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Render based on view mode */}
          {viewMode === 'compact' && (
            // Table View
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredTerms.map((term) => (
                <div
                  key={term.id}
                  className="bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden hover:bg-gray-200 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => openTermDialog(term)}
                >
                  <div className="flex items-center px-4 py-3 gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(term.id);
                      }}
                      className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600 flex-shrink-0"
                    >
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    </Button>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-gray-800 dark:text-white font-medium truncate">
                          {term.term}
                        </h3>
                        <span className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded-full flex-shrink-0">
                          {getCategoryName(term.categoryId)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {viewMode === 'tooltip' && (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1">
              {filteredTerms.map((term) => (
                <div
                  key={term.id}
                  className="flex items-center bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-700/70 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 rounded-lg p-2 cursor-pointer"
                  onClick={() => openTermDialog(term)}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(term.id);
                    }}
                    className="h-6 w-6 p-0 mr-2 hover:bg-gray-200 dark:hover:bg-gray-600 flex-shrink-0"
                  >
                    <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                  </Button>
                  
                  <span className="text-sm text-gray-800 dark:text-white truncate flex-1 font-medium">
                    {term.term}
                  </span>
                  
                  <div className="w-2 h-2 bg-indigo-500 rounded-full ml-2 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {viewMode === 'expanded' && (
            // Card View
            <div className="space-y-4">
              {filteredTerms.map((term) => (
                <div
                  key={term.id}
                  className="bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <div
                    className="bg-indigo-600/80 p-3 flex justify-between items-center cursor-pointer"
                    onClick={() => toggleTermExpansion(term.id)}
                  >
                    <h3 className="text-lg font-semibold text-white">{term.term}</h3>
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(term.id);
                        }}
                        className="text-white hover:bg-indigo-700"
                      >
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      </Button>
                      <ChevronDown
                        className={`h-5 w-5 text-white transition-transform ${
                          expandedTerms[term.id] ? 'transform rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                  {expandedTerms[term.id] && (
                    <div className="p-3 bg-white dark:bg-gray-900">
                      <div
                        className="prose dark:prose-invert max-w-none prose-sm text-gray-700 dark:text-gray-300"
                        dangerouslySetInnerHTML={{ __html: term.content.replace(/<\/p><p>/g, '</p><br><p>') }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* No results */}
          {filteredTerms.length === 0 && searchQuery && (
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
              <p className="text-gray-600 dark:text-gray-400">
                No favorite terms found matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      )}
{/* Term Definition Modal */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              {selectedTerm?.term}
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Category: {selectedTerm ? getCategoryName(selectedTerm.categoryId) : 'General'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div
              className="prose dark:prose-invert max-w-none prose-sm text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{
                __html: selectedTerm?.content.replace(/<\/p><p>/g, '</p><br><p>') || ''
              }}
            />
          </div>
          
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-300 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => selectedTerm && toggleFavorite(selectedTerm.id)}
              className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              Remove from Favorites
            </Button>
            
            <Button
              onClick={() => setIsDialogOpen(false)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNewCategoryName('');
                setShowNewCategoryDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Category Dialog */}
      <Dialog open={showEditCategoryDialog} onOpenChange={setShowEditCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNewCategoryName('');
                setSelectedCategory(null);
                setShowEditCategoryDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateCategory} disabled={!newCategoryName.trim()}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Category Dialog */}
      <Dialog open={showDeleteCategoryDialog} onOpenChange={setShowDeleteCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to delete the category "{selectedCategory?.name}"?
              This will remove all terms from this category.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedCategory(null);
                setShowDeleteCategoryDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GlossaryFavorites;